import {
  type BlockButtons,
  type BlockMedia,
  ChatCustomBlockTypes,
  MediaBlockMediaTypes,
  TextFormResponseBlockRole,
} from "@do/shared-types";
import { cn } from "@do/ui/lib/utils";
import { ChevronRightIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { MAX_CARD_BUTTONS } from "../../../../constants";
import { type CombinedContainer } from "../../../../lib/combine-blocks-and-containers";
import { useChat } from "../../../../providers/chat-provider";
import { getButtonType } from "../buttons";
import { buttonIcons } from "../buttons/button-icon";
import { createMessageButtonClickHandler } from "../buttons/fast-action";
import { MediaIcon } from "../media";

interface MessageCardProps {
  id: string;
  block: CombinedContainer;
  isInContainer?: boolean;
}

export const MessageCard = ({ id, block, isInContainer = false }: MessageCardProps) => {
  const {
    disabled,
    onFastAction,
    footerContent,
    rewriteUrl,
    setLastClickedButton,
    handleBackToPreviousStep,
    isMobile,
  } = useChat();

  const title = useMemo(() => {
    const titleBlock = block.blocks?.find(
      (block) =>
        block.type === ChatCustomBlockTypes.text &&
        "role" in block &&
        block.role === TextFormResponseBlockRole.Title
    );

    if (!titleBlock || !("text" in titleBlock)) return undefined;
    return titleBlock.text as string;
  }, [block.blocks]);

  const subtitle = useMemo(() => {
    const subtitleBlock = block.blocks?.find(
      (block) =>
        block.type === ChatCustomBlockTypes.text &&
        "role" in block &&
        block.role === TextFormResponseBlockRole.Description
    );

    if (!subtitleBlock || !("text" in subtitleBlock)) return undefined;
    return subtitleBlock.text as string;
  }, [block.blocks]);

  const media = useMemo(() => {
    const mediaBlock = block.blocks?.find((block) => block.type === ChatCustomBlockTypes.media);

    if (!mediaBlock) return undefined;
    return mediaBlock as BlockMedia;
  }, [block.blocks]);

  const buttons = useMemo(() => {
    const buttonsBlocks = block.blocks?.filter(
      (block) => block.type === ChatCustomBlockTypes.buttons
    );
    if (!buttonsBlocks || !buttonsBlocks.length) return undefined;
    return buttonsBlocks
      .map((block) => ("buttons" in block ? block.buttons : []))
      .flat() as BlockButtons["buttons"];
  }, [block.blocks]);

  const isCardButtons = useMemo(() => {
    if (!("layout_options" in block) || !block.layout_options) return false;
    return (
      (block.layout_options as Record<string, unknown>)["isButton"] === true &&
      buttons &&
      buttons.length === 1
    );
  }, [block, buttons]);

  const cardButton = isCardButtons ? buttons?.[0] : undefined;
  const cardButtonType = cardButton ? getButtonType(cardButton) : "payload";
  const cardButtonId = cardButton?.html_id ?? `${block.id}-card-button-0`;

  const chatActions = useMemo(
    () => ({
      handleBackToPreviousStep,
      onFastAction,
      rewriteUrl,
      setLastClickedButton,
      isMobile,
    }),
    [handleBackToPreviousStep, isMobile, onFastAction, rewriteUrl, setLastClickedButton]
  );

  const handleCardButtonClick = useCallback(() => {
    if (!cardButton) return;
    createMessageButtonClickHandler(chatActions, cardButton, {
      id: cardButtonId,
      buttonType: cardButtonType,
    })();
  }, [cardButton, cardButtonId, cardButtonType, chatActions]);

  const Component = isCardButtons ? "button" : "div";

  const titleId = `message-${block.id}-card-content-title`;
  const subtitleId = `message-${block.id}-card-content-subtitle`;
  const isDisabled = disabled || !!footerContent;

  return (
    <Component
      id={id}
      {...(!isCardButtons ? { role: "group" } : {})}
      {...(title ? { "aria-labelledby": titleId } : {})}
      {...(subtitle ? { "aria-describedby": subtitleId } : {})}
      {...(isCardButtons ? { type: "button" as const } : {})}
      className={cn(
        "w-full overflow-hidden flex flex-col text-start select-none",
        "rounded-(--card-border-radius)",
        "border-(--card-border-color)/(--card-border-opacity) border-(length:--card-border-width)",
        "bg-(--card-background)/(--card-background-opacity)",
        "[box-shadow:var(--card-box-shadow)]",
        !isInContainer && "@xl/messages:max-w-[350px]",
        isCardButtons &&
          cn(
            "transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            "hover:bg-(--card-hover-background)/(--card-hover-background-opacity)",
            "hover:border-(--card-hover-border-color)/(--card-hover-border-opacity)",
            "hover:[box-shadow:var(--card-hover-box-shadow)]"
          )
      )}
      {...(isCardButtons ? { disabled: isDisabled, onClick: handleCardButtonClick } : {})}
    >
      {media?.mediaType === MediaBlockMediaTypes.Icon && (
        <div id={`message-${block.id}-card-media`} className="p-(--card-content-padding) pb-0">
          <div
            className={cn(
              "flex w-fit items-center justify-center",
              "p-(--card-icon-padding) rounded-(--card-icon-border-radius)",
              "bg-(--card-icon-background) text-(--card-icon-color)"
            )}
          >
            <MediaIcon
              id={`message-${block.id}-card-media`}
              media={media}
              className="size-(--card-icon-size)"
            />
          </div>
        </div>
      )}
      {media && media.mediaType !== MediaBlockMediaTypes.Icon && (
        <div
          id={`message-${block.id}-card-media`}
          className={cn(
            "w-full",
            "p-(--card-image-padding) pb-0",
            "rounded-(--card-image-border-radius)"
          )}
        >
          <img
            id={`message-${block.id}-card-media-image`}
            alt={media?.caption ?? ""}
            src={media?.url ?? undefined}
            className={cn(
              "w-full object-cover object-center",
              "h-(--card-image-height)",
              "rounded-(--card-image-border-radius)"
            )}
          />
        </div>
      )}

      <div
        id={`message-${block.id}-card-content`}
        className={cn(
          "flex flex-col flex-1 justify-between",
          "p-(--card-content-padding) gap-(--card-content-gap)"
        )}
      >
        <div
          id={`message-${block.id}-card-content-titles`}
          className="flex flex-col gap-(--card-titles-gap)"
        >
          <h3
            id={titleId}
            className={cn(
              "text-(length:--card-title-font-size)",
              "font-(--card-title-font-weight)!",
              "font-(family-name:--card-title-font-family)",
              "text-(--card-title-color)/(--card-title-color-opacity)"
            )}
          >
            {title}
          </h3>
          {subtitle && (
            <p
              id={subtitleId}
              className={cn(
                "line-clamp-(--card-subtitle-line-clamp)",
                "text-(length:--card-subtitle-font-size)",
                "font-(--card-subtitle-font-weight)!",
                "font-(family-name:--card-subtitle-font-family)",
                "text-(--card-subtitle-color)/(--card-subtitle-color-opacity)"
              )}
            >
              {subtitle}
            </p>
          )}
        </div>

        {buttons && buttons.length > 0 && !isCardButtons && (
          <div
            id={`message-${block.id}-card-content-buttons`}
            className={cn(
              "flex flex-col divide-y overflow-hidden",
              "rounded-(--card-buttons-border-radius)",
              "divide-(length:--card-buttons-divider-width)",
              "divide-(--card-buttons-divider-color)/(--card-buttons-divider-opacity)",
              "bg-(--card-buttons-background)/(--card-buttons-background-opacity)",
              "[box-shadow:var(--card-buttons-box-shadow)]"
            )}
          >
            {buttons?.slice(0, MAX_CARD_BUTTONS).map((button, index) => {
              const buttonType = getButtonType(button);

              if (buttonType === "store") return null;

              const metadataStr = JSON.stringify(Object.assign({}, ...(button.metadata || [])));
              const buttonId = button.html_id ?? `${block.id}-card-button-${index}`;
              const handleClick = createMessageButtonClickHandler(chatActions, button, {
                id: buttonId,
                buttonType,
              });
              const buttonProps = {
                className: cn(
                  "flex text-start gap-(--card-buttons-gap) items-center justify-between transition-colors duration-200 ease-in-out",
                  "text-(--card-buttons-color)/(--card-buttons-color-opacity)",
                  "text-(length:--card-buttons-font-size)",
                  "font-(--card-buttons-font-weight)!",
                  "font-(family-name:--card-buttons-font-family)",
                  "py-(--card-buttons-padding-y) px-(--card-buttons-padding-x)",
                  "hover:bg-(--card-buttons-hover-background)/(--card-buttons-hover-background-opacity)",
                  "hover:text-(--card-buttons-hover-color)/(--card-buttons-hover-color-opacity)"
                ),
                "data-button-metadata": metadataStr,
                disabled: isDisabled,
              };

              const Icon =
                buttonType in buttonIcons
                  ? buttonIcons[buttonType as keyof typeof buttonIcons]
                  : null;

              return (
                <button
                  id={`message-${block.id}-card-content-button-${button.id}`}
                  onClick={handleClick}
                  key={button.title}
                  {...buttonProps}
                >
                  <span
                    id={`message-${block.id}-card-content-button-${button.id}-content`}
                    className="flex gap-(--card-buttons-gap) items-center"
                  >
                    {Icon && (
                      <Icon
                        id={`message-${block.id}-card-content-button-${button.id}-icon`}
                        aria-hidden="true"
                        className="size-(--card-buttons-icon-size) text-(--card-buttons-icon-color)/(--card-buttons-icon-color-opacity)"
                      />
                    )}
                    <span id={`message-${block.id}-card-content-button-${button.id}-title`}>
                      {button.title}
                    </span>
                  </span>
                  <ChevronRightIcon
                    id={`message-${block.id}-card-content-button-${button.id}-chevron`}
                    aria-hidden="true"
                    className={cn(
                      "shrink-0",
                      "size-(--card-buttons-chevron-size)",
                      "text-(--card-buttons-chevron-color)/(--card-buttons-chevron-color-opacity)"
                    )}
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Component>
  );
};
